import { ErrorHandler, Injectable } from '@angular/core';
import { LoggingService } from './services/logging.service';
import { Router } from '@angular/router';
import { InteractionRequiredAuthError, BrowserAuthError, AuthError } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler implements ErrorHandler {
  constructor(private loggingService: LoggingService, private router: Router) {
  }

  handleError(error, showDetails: boolean = true): void {
    // Unwrap the error if it's wrapped by Angular/zone.js
    const originalError = error?.rejection || error;

    // If this is an MSAL authentication error (e.g. expired token after inactivity),
    // don't redirect to the error page — let MSAL handle reauthentication via redirect.
    if (this.isMsalAuthError(originalError)) {
      console.warn('MSAL authentication error detected — suppressing error page, reauthentication in progress.', originalError);
      this.loggingService.logError(originalError);
      return;
    }

    this.loggingService.logError(error);
    this.router.navigate(['/error'], { replaceUrl: true, state: { message: error.message, showDetails: showDetails } });
  }

  handleErrorNoRedirect(error): void {
    this.loggingService.logError(error);
  }

  private isMsalAuthError(error: unknown): boolean {
    if (!error) {
      return false;
    }

    // Direct MSAL error type checks
    if (error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError ||
        error instanceof AuthError) {
      return true;
    }

    // Check by error name for cases where instanceof doesn't match (e.g. multiple MSAL bundles)
    const err = error as { name?: string; message?: string };
    const errorName = err.name || '';
    if (errorName === 'InteractionRequiredAuthError' ||
        errorName === 'BrowserAuthError' ||
        errorName === 'ClientAuthError' ||
        errorName === 'ClientConfigurationError') {
      return true;
    }

    // Check for common MSAL error messages in the error string
    const errorMessage = (String(err?.message ?? "") || String(error) || '').toLowerCase();
    const msalErrorPatterns = [
      'interaction_required',
      'login_required',
      'consent_required',
      'no_account_error',
      'monitor_window_timeout',
      'user_cancelled',
      'interaction_in_progress',
      'no_tokens_found',
      'endpoints_resolution_error',
      'uninitialized_public_client_application',
    ];

    return msalErrorPatterns.some(pattern => errorMessage.includes(pattern));
  }
}

